import { Alert } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { EditItem } from "ui/Scripts/source/models/common/editItem";
import { IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import { IRecordEditItemTransferredProperty, RecordEditItemTransferredProperty } from "../../../models/record/recordEditItemTransferredProperty";
import * as recordService from "../../../models/services/recordService";
import { ActionContainer } from "../../framework/ActionContainer";
import { List } from "../../framework/List/List";
import { ListIconButtonsItem } from "../../framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { RecordEditListFirstCol } from "./RecordEditListFirstCol";
import { RecordUserTransferredPropertyDialog } from "../RecordUserTransferredPropertyDialog/RecordUserTransferredPropertyDialog";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

export interface IRecordEditTransferredPropertyListProp {
    isReadOnly: boolean;
    companyId: string;
    recordId: string;
    userCode: string;
    transferredPropertyTypes: string[];
    items: IRecordEditItemTransferredProperty[];
    attachments: IFileAttachment[];
    onRemoved: (ids: string[]) => void;
    onSaved: (employment: IRecordEditItemTransferredProperty) => void;
    onAttachmentsRemoved: (ids: string[]) => void;
    onAttachmentsAdded: (attachments: IFileAttachment[]) => void;
}

interface IRecordEditTransferredPropertyListState {
    // List
    isLoading: boolean;
    items: IRecordEditItemTransferredProperty[];
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    selectedId: string;
    checkedIds: string[];
    selectedItemHasPrevious: boolean;
    selectedItemHasNext: boolean;
    transferredPropertyTypes: string[];

    // Dialog
    item: IRecordEditItemTransferredProperty;
    showEditDialog: boolean;
}

export class RecordEditTransferredPropertyList extends React.Component<IRecordEditTransferredPropertyListProp, IRecordEditTransferredPropertyListState> {
    getFilteredList = (filter: string, column: string, asc: boolean): IRecordEditItemTransferredProperty[] => {
        const props = this.props;
        let result = props.items ? props.items.slice(0) : [];
        if (filter) {
            const lowFilter = filter.toLowerCase();
            result = result.filter(i => i.type.toLowerCase().indexOf(lowFilter) > -1 || i.code.toLowerCase().indexOf(lowFilter) > -1 || i.description.toLowerCase().indexOf(lowFilter) > -1);
        }
        RecordEditItemTransferredProperty.sortRecordEditItemTransferredProperties(result, column, asc);
        return result;
    };

    constructor(props: IRecordEditTransferredPropertyListProp) {
        super(props);
        const items = this.getFilteredList("", "returnDate", false);
        this.state = {
            isLoading: false,
            items,
            filter: "",
            sortColumn: "returnDate",
            sortOrderIsAsc: false,
            selectedId: null,
            checkedIds: [],
            selectedItemHasPrevious: false,
            selectedItemHasNext: false,
            item: null,
            showEditDialog: false,
            transferredPropertyTypes: Base.getUniqueStringItems(props.transferredPropertyTypes.concat(props.items.map(i => i.type).filter(i => !!i)))
        };
    }

    filteredList = (filter: string, column: string, asc: boolean) => {
        const items = this.getFilteredList(filter, column, asc);
        const item = this.state.showEditDialog && this.state.item
            ? items.find(i => i.id === this.state.item.id)
            : null;
        const index = item ? items.findIndex(i => i.id === item.id) : -1;
        this.setState({
            filter,
            sortColumn: column,
            sortOrderIsAsc: asc,
            items,
            item,
            showEditDialog: !!item,
            selectedItemHasPrevious: index > 0,
            selectedItemHasNext: index < items.length - 1,
        });
    };

    componentDidUpdate(prevProps: IRecordEditTransferredPropertyListProp, prevState: IRecordEditTransferredPropertyListState) {
        const props = this.props;
        const state = this.state;
        if (EditItem.getHash(prevProps.items) === EditItem.getHash(props.items)) return;
        this.filteredList(state.filter, state.sortColumn, state.sortOrderIsAsc);
        this.setState({
            transferredPropertyTypes: Base.getUniqueStringItems(props.transferredPropertyTypes.concat(props.items.map(i => i.type).filter(i => !!i)))
        });
    };

    changeFilter = (filter: string) => {
        const state = this.state;
        this.filteredList(filter, state.sortColumn, state.sortOrderIsAsc);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.filteredList(state.filter, sortColumn, sortOrderIsAsc);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    getRemoveSaveData = (props: IRecordEditTransferredPropertyListProp, recordEmploymentIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.recordId);
        result.append("companyId", props.companyId);
        result.append("userCode", props.userCode);
        result.append("ids", recordEmploymentIds);
        return result;
    };

    handleRemove = async () => {
        const obj = this;
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        if (selectedIds.length < 1) return;
        const item = state.items.find(i => i.id === selectedIds[0]);
        if (!item) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveRecordUserTransferredPropertyParameter, item.code), false) === ConfirmationDialogResult.Yes) {
            const saveData = this.getRemoveSaveData(this.props, selectedIds);
            // Call server
            const result = await AppUtils.callService(() => recordService.recordEditRemoveTransferredProperties(saveData.obj));
            if (!result) return;
            obj.props.onRemoved(selectedIds);
        }
    };
    
    handleAdd = () => {
        const editItem = new RecordEditItemTransferredProperty();
        editItem.id = Base.getGuid();
        editItem.transferDate = Base.getNowUtcDate().getTime();
        this.setState({
            item: editItem,
            showEditDialog: true
        });
    };

    handleOpen = (id: string) => {
        if (!id) return;
        const state = this.state;
        const index = state.items.findIndex(i => i.id === id);
        if (index < 0) return;
        this.setState({
            selectedId: id,
            item: state.items[index],
            showEditDialog: true,
            selectedItemHasPrevious: index > 0,
            selectedItemHasNext: index < state.items.length - 1
        });
    };

    onRecordUserEmploymentDialogSaved = async (item: IRecordEditItemTransferredProperty, closeDialog: boolean) => {
        if (!item) return;
        this.setState({
            showEditDialog: !closeDialog,
            selectedId: item.id,
            item
        });
        this.props.onSaved(item);
    };

    onRecordUserEmploymentDialogClose = () => {
        this.setState({
            showEditDialog: false
        });
    };

    setEditDialogItemByIndex = (index: number) => {
        const state = this.state;
        if (index < 0 || index >= state.items.length) return;
        this.setState({
            selectedId: state.items[index].id,
            item: state.items[index],
            selectedItemHasPrevious: index > 0,
            selectedItemHasNext: index < state.items.length - 1
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <>
                <VerticalContainer
                    before={<>
                        <ActionContainer
                            leftItems={
                                <ListSearchFilter
                                    searchFilter={state.filter}
                                    onSearchClick={this.changeFilter}
                                />
                            }
                            rightItems={
                                <ListIconButtonsItem
                                    addTooltip={Translations.AddTransferredProperty}
                                    removeTooltip={Translations.RemoveTransferredProperty}
                                    addDisabled={props.isReadOnly}
                                    removeDisabled={!state.selectedId && state.checkedIds.length < 1}
                                    onAdd={this.handleAdd}
                                    onRemove={this.handleRemove}
                                />
                            }
                        />
                        {!!props.isReadOnly &&
                            <Alert severity="info" sx={{ marginBottom: "1rem" }}>{Translations.NewRecordUserMustBeSavedBeforeTransferredPropertiesCanBeAdded}</Alert>
                        }
                    </>}
                    main={<>
                        {!props.isReadOnly &&
                            <List
                                intialized={true}
                                isLoading={state.isLoading}
                                hasMore={false}
                                globalLoadingInUse={false}
                                height="100%"
                                header={{
                                    columns: [
                                        { size: 2, column: "type", content: Translations.AType }, // "col-xl-2 col-2"
                                        { size: 2, column: "code", content: Translations.Title }, // "col-xl-2 col-2"
                                        { size: 4, column: "description", content: Translations.AdditionalInformation }, // "col-xl-5 col-4"
                                        { size: 2, column: "transferDate", content: Translations.PropertyTransferred }, // "col-xl-1 col-2"
                                        { size: 2, column: "returnDate", content: Translations.Returned + " / " + Translations.Removed } // "col-xl-2 col-2"
                                    ]
                                }}
                                lines={
                                    state.items.map((item) => {
                                        return {
                                            columns: [
                                                {
                                                    size: 2,
                                                    content:
                                                        <RecordEditListFirstCol
                                                            id={item.id}
                                                            code={item.type}
                                                            attachments={props.attachments}
                                                            onClick={this.handleOpen}
                                                        />
                                                },
                                                { size: 2, content: item.code },
                                                { size: 4, content: item.description },
                                                { size: 2, content: Base.utcTimeToDateStr(item.transferDate) },
                                                { size: 2, content: Base.utcTimeToDateStr(item.returnDate) }
                                            ],
                                            id: item.id
                                        };
                                    })
                                }
                                noDataMessage={Translations.AddTransferredProperties}
                                sortColumn={state.sortColumn}
                                sortOrderIsAsc={state.sortOrderIsAsc}
                                selectedId={state.selectedId}
                                checkedIds={state.checkedIds}
                                onSetSelectedIds={this.handleSetSelectedIds}
                                onSearchLines={this.handleSearchLines}
                                onOpenLine={this.handleOpen}
                            />
                        }
                    </>}
                />
                {state.showEditDialog &&
                    <RecordUserTransferredPropertyDialog
                        companyId={props.companyId}
                        recordId={props.recordId}
                        userCode={props.userCode}
                        transferredPropertyTypes={state.transferredPropertyTypes}
                        item={state.item}
                        attachments={props.attachments}
                        hasPrevious={state.selectedItemHasPrevious}
                        hasNext={state.selectedItemHasNext}
                        onSaved={this.onRecordUserEmploymentDialogSaved}
                        onClose={this.onRecordUserEmploymentDialogClose}
                        onPrevious={() => List.moveToPrevious(state, this.setEditDialogItemByIndex)}
                        onNext={() => List.moveToNext(state, this.setEditDialogItemByIndex)}
                        onAttachmentsRemoved={props.onAttachmentsRemoved}
                        onAttachmentsAdded={props.onAttachmentsAdded}
                    />
                }
            </>
        );
    }
}